/* Almacenamiento persistente con IndexedDB (librería idb).

   Reemplaza al antiguo `window.storage` del prototipo (artifact) conservando el
   mismo esquema conceptual: un ÍNDICE con el orden de las capturas + una entrada
   por CAPTURA (ver S-005 en docs/SOLUCIONES.md).

   - store "capturas": una entrada por captura, con clave `id`.
   - store "meta": guarda el arreglo `orden` (ids en orden de creación), que
     define la numeración REV-### y el orden de la galería y el informe.

   IndexedDB admite mucho más espacio que window.storage y permitiría blobs en el
   futuro; por ahora las imágenes siguen viajando como dataURL (JPEG reducido)
   para no cambiar el formato de los datos existentes ni del informe. */

import { openDB } from "idb";

const DB_NOMBRE = "revisor-web";
const DB_VERSION = 1;
const STORE_CAPTURAS = "capturas";
const STORE_META = "meta";
const CLAVE_ORDEN = "orden";

let _dbPromise = null;

function db() {
  if (!_dbPromise) {
    _dbPromise = openDB(DB_NOMBRE, DB_VERSION, {
      upgrade(database) {
        if (!database.objectStoreNames.contains(STORE_CAPTURAS)) {
          database.createObjectStore(STORE_CAPTURAS, { keyPath: "id" });
        }
        if (!database.objectStoreNames.contains(STORE_META)) {
          database.createObjectStore(STORE_META);
        }
      },
    });
  }
  return _dbPromise;
}

async function leerOrden(database) {
  const orden = await database.get(STORE_META, CLAVE_ORDEN);
  return Array.isArray(orden) ? orden : [];
}

// Devuelve todas las capturas en el orden del índice. Tolera índices
// desincronizados: agrega al final cualquier captura presente en el store pero
// ausente del orden, y omite ids del orden sin captura.
export async function cargarCapturas() {
  try {
    const database = await db();
    const [orden, todas] = await Promise.all([
      leerOrden(database),
      database.getAll(STORE_CAPTURAS),
    ]);
    const porId = new Map(todas.map((c) => [c.id, c]));
    const resultado = [];
    const vistos = new Set();
    for (const id of orden) {
      const cap = porId.get(id);
      if (cap) {
        resultado.push(cap);
        vistos.add(id);
      }
    }
    for (const cap of todas) {
      if (!vistos.has(cap.id)) resultado.push(cap);
    }
    return resultado;
  } catch (e) {
    return [];
  }
}

// Inserta o actualiza una captura y mantiene el índice de orden.
// `idsActuales` es el orden vigente en memoria (mismo criterio que el prototipo).
export async function guardarCaptura(cap, idsActuales) {
  try {
    const database = await db();
    const tx = database.transaction([STORE_CAPTURAS, STORE_META], "readwrite");
    await tx.objectStore(STORE_CAPTURAS).put(cap);
    if (!idsActuales.includes(cap.id)) {
      await tx.objectStore(STORE_META).put([...idsActuales, cap.id], CLAVE_ORDEN);
    }
    await tx.done;
    return true;
  } catch (e) {
    return false;
  }
}

// Elimina una captura y la quita del índice de orden.
export async function borrarCaptura(id, idsActuales) {
  try {
    const database = await db();
    const tx = database.transaction([STORE_CAPTURAS, STORE_META], "readwrite");
    await tx.objectStore(STORE_CAPTURAS).delete(id);
    await tx
      .objectStore(STORE_META)
      .put(idsActuales.filter((x) => x !== id), CLAVE_ORDEN);
    await tx.done;
    return true;
  } catch (e) {
    return false;
  }
}
